import { useEffect } from 'react';

import { Box, Card, CardContent, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Theme } from '@truvhq/ui';
import { LoadingScreen, colors } from '@truvhq/ui';
import { Controller, useForm } from 'react-hook-form';

import type { OnboardingFormData, OnboardingInfo } from './api';
import { saveOnboardingInfo } from './api';

import { useGetProfile, useProfileSuggest } from 'hooks/profile';
import { useAuth } from 'hooks/useAuth';

import { AnalyticsButton } from 'components/AnalyticsButton';
import { Select } from 'components/Select';

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    width: '100%',
  },
  cardContent: {
    padding: '32px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  companyRow: {
    width: '100%',
    display: 'flex',
  },
  companyContainer: {
    flexGrow: 1,
    paddingRight: '8px',
    margin: '8px 0',
  },
  divider: {
    margin: '32px -32px',
    backgroundColor: colors.grey30,
  },
  fieldsContainer: {
    width: '80%',
    maxWidth: '448px',
  },
  formContainer: {
    backgroundColor: theme.palette.background.paper,
  },
  headerContainer: {
    width: '100%',
    textAlign: 'center',
  },
  header: {
    marginBottom: '16px',
  },
  input: {
    margin: '16px 0 0 0',
  },
  fieldHint: { fontSize: '14px', color: theme.palette.text.secondary },
  notifySwitch: {
    lineHeight: '32px',
  },
  notifySwitchContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  optionalHint: {
    color: colors.grey50,
  },
}));

type ProfileFormProps = {
  submitting: boolean;
  isLoading: boolean;
  showCompanyField: boolean;
  onSubmit: (formData: OnboardingFormData) => void;
};

const markets = [
  { name: 'Employment verifications', value: 'employment verifications' },
  { name: 'VOIE for mortgage', value: 'voie for mortgage' },
  { name: 'VOIE for consumer & auto', value: 'voie for consumer' },
  { name: 'Tenant screening', value: 'tenant screening' },
  { name: 'Building payroll integrations', value: 'payroll integrations' },
  { name: 'Direct deposit switch', value: 'dds' },
];

const roles = [
  { name: 'Developer', value: 'developer' },
  { name: 'Product', value: 'product' },
  { name: 'Operations', value: 'operations' },
  { name: 'Other', value: 'other' },
];

const ProfileForm = ({ submitting, isLoading, onSubmit, showCompanyField }: ProfileFormProps) => {
  const classes = useStyles();
  const { suggest } = useProfileSuggest();

  const { handleSubmit, control, errors, reset } = useForm({
    mode: 'onChange',
  });

  useEffect(() => {
    reset({
      companyName: suggest?.client_name,
      firstName: suggest?.first_name,
      lastName: suggest?.last_name,
    });
  }, [suggest]);

  const isDisabled = submitting || isLoading || Object.keys(errors).length !== 0;

  return (
    <form className={classes.card} onSubmit={handleSubmit(onSubmit)}>
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <Box className={classes.headerContainer}>
            <Typography className={classes.header} variant="h1">
              Tell us about yourself
            </Typography>
          </Box>

          {isLoading ? (
            <Box display="flex" justifyContent="center" my={5}>
              <CircularProgress />
            </Box>
          ) : (
            <Box className={classes.fieldsContainer}>
              <Controller
                fullWidth
                as={TextField}
                className={classes.input}
                control={control}
                error={errors.firstName != null}
                helperText={errors.firstName?.type === 'required' && 'First name is required'}
                label="First Name"
                name="firstName"
                rules={{
                  required: true,
                }}
                variant="outlined"
              />

              <Box>
                <Controller
                  fullWidth
                  as={TextField}
                  className={classes.input}
                  control={control}
                  error={errors.lastName != null}
                  helperText={errors.lastName?.type === 'required' && 'Last name is required'}
                  label="Last Name"
                  name="lastName"
                  rules={{
                    required: true,
                  }}
                  variant="outlined"
                />
              </Box>
              {showCompanyField && (
                <Box>
                  <Controller
                    fullWidth
                    as={TextField}
                    className={classes.input}
                    control={control}
                    error={errors.companyName != null}
                    helperText={errors.companyName?.type === 'required' && 'Company name is required'}
                    label="Company name"
                    name="companyName"
                    rules={{
                      required: true,
                    }}
                    variant="outlined"
                  />
                </Box>
              )}
              <Box>
                <Controller
                  as={
                    <Select fullWidth id="type" label="What problem are you solving?">
                      {markets.map((market) => (
                        <MenuItem key={market.value} value={market.value}>
                          {market.name}
                        </MenuItem>
                      ))}
                    </Select>
                  }
                  className={classes.input}
                  control={control}
                  error={errors.type != null}
                  helperText={errors.type?.type === 'required' && 'Please specify the problem'}
                  name="type"
                  rules={{
                    required: true,
                  }}
                />
                <Controller
                  as={
                    <Select fullWidth id="role" label="What role are you in?">
                      {roles.map((role) => (
                        <MenuItem key={role.value} value={role.value}>
                          {role.name}
                        </MenuItem>
                      ))}
                    </Select>
                  }
                  className={classes.input}
                  control={control}
                  error={errors.role != null}
                  helperText={errors.role?.type === 'required' && 'Role is required'}
                  name="role"
                  rules={{
                    required: true,
                  }}
                />
              </Box>
              <Box mb={1} mt={4}>
                <AnalyticsButton
                  fullWidth
                  color="primary"
                  disabled={isDisabled}
                  eventName="onboarding - click continue"
                  size="large"
                  type="submit"
                  variant="contained"
                >
                  Continue to dashboard
                </AnalyticsButton>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </form>
  );
};

type OnboardingProfileProps = {
  showCompanyField: boolean;
};

export const OnboardingProfile = ({ showCompanyField }: OnboardingProfileProps) => {
  const { update } = useAuth();

  const { data: profileSettings, isLoading } = useGetProfile();

  const queryCache = useQueryClient();

  const mutation = useMutation({
    mutationFn: (newProfileData: OnboardingInfo) => {
      return saveOnboardingInfo(newProfileData, {
        updateCompany: showCompanyField,
        companyId: profileSettings?.company?.id,
      });
    },
    onSuccess: ([data]) => {
      queryCache.setQueryData(['getProfile'], () => {
        return data;
      });
      update();
    },
    onError: (error) => {
      console.error(error);
    },
  });

  if (isLoading || !profileSettings || mutation.isLoading || mutation.isSuccess) {
    return <LoadingScreen />;
  }

  const onSubmit = (values: OnboardingFormData) => {
    const submittedData = { ...values, email: profileSettings.email };

    mutation.mutate(submittedData);
  };

  return (
    <>
      <ProfileForm
        isLoading={isLoading}
        showCompanyField={showCompanyField}
        submitting={mutation.isLoading}
        onSubmit={onSubmit}
      />
    </>
  );
};
